import ItinearyTextField from '@/src/components/common/itinerary/itinearyTextField'
import { getLinkByName } from '@/src/components/mobile/homepageFooter/footerLinks'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Typography
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import GoogleSignInButton from '../../../../GoogleSignInButton'
import SendToMobileIcon from '@mui/icons-material/SendToMobile'

interface SignUpFormProps {
    form: any
    status: any
    handlers: any
    t: any
    showGoogleSignIn?: boolean
    onGoogleSignIn: (userData: {
        email: string
        firstName: string
        lastName: string
        fullName: string
        googleId: string
        picture?: string
    }) => void
    setActiveTab: (tab: number) => void
}

const SignUpForm = ({
    form,
    status,
    handlers,
    showGoogleSignIn = true,
    onGoogleSignIn,
    t,
    setActiveTab
}: SignUpFormProps) => {
    const pathname = usePathname()
    const locale = pathname.split('/')[1] || 'en'
    const tTitles = useTranslations('common.titles')
    const tFooter = useTranslations('footer')
    const tOtpModal = useTranslations('otpModal')

    return (

        <Stack spacing={1.2}>

            <Box sx={{ display: 'flex', gap: 1, }}>
                <Box sx={{ flex: 1 }}>
                    <InputLabel
                        shrink
                        htmlFor={`title-select`}
                        sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                    >
                        {t('title')}
                    </InputLabel>
                    <FormControl sx={{ minWidth: 80 }} size="small">
                        {/* <InputLabel id="title-select-label">{tHeader('title')}</InputLabel> */}
                        <Select
                            labelId="title-select-label"
                            id="title-select"
                            value={form.title}
                            // label={tHeader('title')}
                            onChange={e => handlers.handlers.setTitle(e.target.value)}
                            sx={{
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                fontSize: 14,
                                height: 32,
                                '& .MuiSelect-select': { py: 0.5, px: 1.5 }
                            }}
                        >
                            <MenuItem value="Mr">{tTitles('mr')}</MenuItem>
                            <MenuItem value="Ms">{tTitles('ms')}</MenuItem>
                            <MenuItem value="Mrs">{tTitles('mrs')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ flex: 2 }}>
                    <InputLabel
                        shrink
                        htmlFor={`first-name`}
                        sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                    >
                        {t('firstName')}
                    </InputLabel>
                    <ItinearyTextField
                        placeholder={t('firstName')}
                        // fullWidth
                        value={form.firstName}
                        onChange={handlers.handlers.changeFirstNameHandler}
                        error={!form.isValidFirstName}
                        helperText={!form.isValidFirstName ? t('invalidFirstName') : ''}
                        sx={{
                            '& .MuiInputBase-input': { py: 0.5, px: 1.5, fontSize: 13, height: 26, borderRadius: 1 },
                            borderRadius: 1
                        }}
                    />
                </Box>
                <Box sx={{ flex: 2 }}>
                    <InputLabel
                        shrink
                        htmlFor={`last-name`}
                        sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                    >
                        {t('lastName')}
                    </InputLabel>

                    <ItinearyTextField
                        placeholder={t('lastName')}
                        fullWidth
                        value={form.lastName}
                        onChange={handlers.handlers.changeLastNameHandler}
                        error={!form.isValidLastName}
                        helperText={!form.isValidLastName ? t('invalidLastName') : ''}
                    />
                </Box>

            </Box>
            <Box sx={{ flex: 1 }}>
                <InputLabel
                    shrink
                    htmlFor={`email`}
                    sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                >
                    {t('email')}
                </InputLabel>
                <ItinearyTextField
                    placeholder={t('email')}
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={handlers.handlers.changeEmailHandler}
                    error={!form.isEmailValid}
                    helperText={!form.isEmailValid ? t('invalidEmail') : ''}
                />
            </Box>
            <Box sx={{ flex: 1 }}>
                <InputLabel
                    shrink
                    htmlFor={`password`}
                    sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                >
                    {t('password')}
                </InputLabel>
                <ItinearyTextField
                    placeholder={t('password')}
                    type={form.passwordType ? 'password' : 'text'}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handlers.handlers.changePasswordHandler}
                    error={!form.isPasswordValid}
                    helperText={!form.isPasswordValid ? t('invalidPassword') : ''}
                />
            </Box>
            <FormControlLabel
                control={
                    <Checkbox
                        sx={{ p: 0, pr: 0.3, '& .MuiSvgIcon-root': { fontSize: '1.4rem' } }}
                        checked={form.travelCheckedStatus}
                        onChange={e => handlers.handlers.setTravelCheckedStatus(e.target.checked)}
                    />
                }
                label={<Typography sx={{ fontSize: 13 }}>{t('sendMeTravelOffers')}</Typography>}
            />
            <Box display={"flex"} flexDirection={"column"} justifyContent={"space-between"} gap={1} pt={'10px'}>
                <Button
                    variant="contained"
                    sx={{ width: '100%', fontWeight: 600, fontSize: 16 }}
                    onClick={handlers.onSignUp}
                    disabled={status.loading}
                >
                    {status.loading ? <CircularProgress size={24} color="inherit" /> : tOtpModal('continue')}
                </Button>
                {showGoogleSignIn && (
                    <GoogleSignInButton onSuccess={onGoogleSignIn} buttonText="Google" fullWidth />
                )}
                <Button
                    fullWidth
                    variant="customVariantPrimary"
                    onClick={() => setActiveTab(0)}
                    startIcon={<SendToMobileIcon style={{ width: '18px', height: '18px' }} />}
                    sx={{
                        height: 40,
                        fontSize: 14,
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: 'text.primary',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                            backgroundColor: 'grey.50',
                            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)'
                        }
                    }}
                >
                    {tOtpModal('continueWithMobileNumber')}
                </Button>
            </Box>
        </Stack>

    )
}

export default SignUpForm
